import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useSidebar } from "../../../../contexts/SidebarContext";
import useAdminUser from "../../../../hooks/useAdminUser";

import {
  getVisibleAdminSections,
  getFirstAccessibleAdminPath,
  isAllowedAdminPath,
} from "../../../../utils/adminNavigation";

import {
  ADMIN_SIDEBAR_WIDTHS,
  ADMIN_SIDEBAR_MARGINS,
} from "../data/adminShellConfig";

export default function useAdminSidebar() {
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const { user: effectiveUser, permissionsLoaded } = useAdminUser();

  const shellWidth = useMemo(
    () =>
      isCollapsed
        ? ADMIN_SIDEBAR_WIDTHS.collapsed
        : ADMIN_SIDEBAR_WIDTHS.expanded,
    [isCollapsed],
  );

  const mainMargin = useMemo(
    () =>
      isCollapsed
        ? ADMIN_SIDEBAR_MARGINS.collapsed
        : ADMIN_SIDEBAR_MARGINS.expanded,
    [isCollapsed],
  );

  const visibleSections = useMemo(() => {
    if (!permissionsLoaded) return [];
    return getVisibleAdminSections(effectiveUser);
  }, [effectiveUser, permissionsLoaded]);

  const firstAccessiblePath = useMemo(() => {
    if (!permissionsLoaded) return null;
    return getFirstAccessibleAdminPath(effectiveUser);
  }, [effectiveUser, permissionsLoaded]);

  const currentAdminPathAllowed = useMemo(() => {
    if (!location.pathname.startsWith("/appli")) return true;
    if (!permissionsLoaded) return true;
    return isAllowedAdminPath(effectiveUser, location.pathname);
  }, [effectiveUser, location.pathname, permissionsLoaded]);

  useEffect(() => {
    if (!location.pathname.startsWith("/appli")) return;
    if (!permissionsLoaded) return;

    if (!currentAdminPathAllowed) {
      if (firstAccessiblePath && firstAccessiblePath !== location.pathname) {
        navigate(firstAccessiblePath, { replace: true });
        return;
      }

      if (!firstAccessiblePath && location.pathname !== "/") {
        navigate("/", { replace: true });
      }
    }
  }, [
    location.pathname,
    currentAdminPathAllowed,
    firstAccessiblePath,
    navigate,
    permissionsLoaded,
  ]);

  const openMobile = () => setMobileOpen(true);
  const closeMobile = () => setMobileOpen(false);
  const toggleCollapsed = () => setIsCollapsed((prev) => !prev);

  return {
    isCollapsed,
    shellWidth,
    mainMargin,
    mobileOpen,
    visibleSections,
    permissionsLoaded,
    pathname: location.pathname,
    openMobile,
    closeMobile,
    toggleCollapsed,
  };
}